import baseComponent from "@gdk/base-component";
import Version from "@gdk/version";
import { IBaseComponentOptions } from "@gdk/base-component";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Radar from "radar-sdk-js";

const component = "Address Auto Complete";
const versions = [
    { version: "4.1.0", release: "11.30.23" },
    { version: "4.0.0", release: "11.2.23" },
    { version: "3.2.0", release: "8.3.23" },
    { version: "3.1.0", release: "6.8.23" },
    { version: "3.0.0", release: "5.25.23" }
];

const validateSettings = [
    {
        setting: "content",
        isRequired: true,
        validate: "type",
        possibleValues: ["string", "object"],
        errorMessage: ["GDK AddressAutoComplete : Content must be defined and set to a DOM selector or Node"]
    },
    {
        setting: "apiKey",
        isRequired: true,
        validate: "type",
        possibleValues: ["string"],
        errorMessage: ["GDK AddressAutoComplete : apiKey must be defined and set to a string"]
    },
    {
        setting: "queryParameters",
        isRequired: false,
        validate: "type",
        possibleValues: ["object"],
        errorMessage: ["GDK AddressAutoComplete : queryParameters must be set to a object"]
    },
    {
        setting: "onSelectAddress",
        isRequired: false,
        validate: "type",
        possibleValues: ["function"],
        errorMessage: ["GDK AddressAutoComplete : onSelectAddress must be set to function"]
    }
];

export interface IAddressAutoCompleteOptions extends IBaseComponentOptions {
    apiKey: string;
    queryParameters?: {
        near?: string, 
        limit?: number,
        country?: string,
        expandUnits?: boolean,
        debounceMS?: number,
        threshold?: number,
        layers?: string;
    };
    onSelectAddress?: () => void;
}

/**
 * @desc GDK Address Auto Complete JavaScript Class
 *
 * @example <caption>JS Instantiation</caption>
 * var  addressAutoComplete = new GDK.AddressAutoComplete({
 *         content: "#address-auto-complete",
 *         apiKey: "prj_live_pk_...",
 *         queryParameters: {
 *              limit: 10,
 *              country: "US, PR", //US and Puerto Rico
 *         },
 *         onSelectAddress: function() {
 *              console.log(AddressAutoComplete.getAddressData())
 *         }
 *     });
 */
class GdkAddressAutoComplete {
    _internalVars: {
        node: HTMLElement;
        contentType: "string" | "number" | "bigint" | "boolean" | "symbol" | "undefined" | "object" | "function";
        selectedAddressObject: object;
    };

    readonly _defaults: {
        queryParameters?: {
            near?: string, 
            limit?: number,
            country?: string,
            expandUnits?: boolean,
            debounceMS?: number,
            threshold?: number,
            layers?: string
        };
    };

    readonly _options: IAddressAutoCompleteOptions;

    /**
     * @param {Object} options Settings for the instantiation.
     *
     * @param {string|HTMLElement} options.content A reference to the component node
     * 
     * @param {string} options.apiKey Required Radar API key
     * 
     * @param {object} [options.queryParameters="{
     *  limit: 10,
     *  country: "US, PR",
     *  layers : "address",
     *  expandUnits : false,
     *  debounceMS : 200,
     *  threshold: 3
     *  }] Query parameters. Refer to the development guidelines in the design system https://hue.geico.com/address-auto-complete
     * 
     * @param {function} [options.onSelectAddress] A callback function triggered when an address is selected from the list
     */
    constructor(options: IBaseComponentOptions) {
        /**
         * @ignore
         */
        this._internalVars = {
            node: null,
            contentType: null,
            selectedAddressObject: {}
        };

        //options with defaults set
        /**
         * @ignore
         */
        this._defaults = {
            queryParameters: {}
        };

        // Create options by extending defaults with the passed in arguments
        if (options && typeof options === "object") {
            /**
             * @ignore
             */
            this._options = baseComponent.extendDefaults(this._defaults, options);
        }

        //if the required options are valid set up the environment
        if (baseComponent.validateSettings(this._options, validateSettings)) {
            this._internalVars.contentType = baseComponent.getContentType(this);
            Radar.initialize(this._options.apiKey);
            _setLocalVars.call(this);
            _setEvents.call(this);
            _init.call(this);
        }
    }

    //Public Methods
    /**
     * @desc returns an object containing the address
     * 
     * @returns {object}
     *
     * @example
     * AddressAutoComplete.getAddressData();
     */
    getAddressData(): object {
        return this._internalVars.selectedAddressObject;
    }
}


function _setLocalVars(): void {
    //determine the type of content passed in
    if (this._internalVars.contentType === "string") {
        this._internalVars.node = document.querySelector(this._options.content);
    } else if (this._internalVars.contentType === "domNode") {
        this._internalVars.node = this._options.content;
    }
    this._internalVars.addressSearchField = this._internalVars.node.querySelector(".address-search-field");
    this._internalVars.addressSearchContainer = this._internalVars.addressSearchField.parentElement;

    this._internalVars.address1Element = this._internalVars.node.querySelector(".address_1");
    this._internalVars.address2Element = this._internalVars.node.querySelector(".address_2");
    this._internalVars.aptNumElement = this._internalVars.node.querySelector(".apt_num");
    this._internalVars.city = this._internalVars.node.querySelector(".locality");
    this._internalVars.state = this._internalVars.node.querySelector(".administrative_area_level_1");
    this._internalVars.postalCode = this._internalVars.node.querySelector(".postal_code");
    this._internalVars.country = this._internalVars.node.querySelector(".country");

    this._internalVars.isOnSuggestions = false;
    this._internalVars.apiKey = this._options.key;
    this._internalVars.selectedAddressObject = {};

    this._internalVars.fillInAddress = _fillInAddress.bind(this);
    this._internalVars.displaySuggestedAddresses = _displaySuggestedAddresses.bind(this);
    this._internalVars.getAddresses = _getAddresses.bind(this);
    this._internalVars.clearSearch = _clearSearch.bind(this);
    this._internalVars.inputAddressSearchFieldEventHandler = _inputAddressSearchFieldEventHandler.bind(this);
    this._internalVars.blurAddressSearchFieldEventHandler = _blurAddressSearchFieldEventHandler.bind(this);
    this._internalVars.focusAddressSearchFieldEventHandler = _focusAddressSearchFieldEventHandler.bind(this);
}


function _setEvents(): void {
    this._internalVars.addressSearchField.addEventListener("input", this._internalVars.inputAddressSearchFieldEventHandler);
    this._internalVars.addressSearchField.addEventListener("blur", this._internalVars.blurAddressSearchFieldEventHandler);
    this._internalVars.addressSearchField.addEventListener("focus", this._internalVars.focusAddressSearchFieldEventHandler);
}


function _init(): void {

    const queryResultsContainer = document.createElement("div");
    queryResultsContainer.classList.add("query-results-container", "hidden");
    this._internalVars.addressSearchContainer.appendChild(queryResultsContainer);
    this._internalVars.queryResultsContainer = queryResultsContainer;
    this._internalVars.addressSearchField.autocomplete = "off";
    this._internalVars.queryResultsContainer = queryResultsContainer;
    this._internalVars.queryResultsContainer.addEventListener("mouseover", _setIsOnSuggestionsTrue.bind(this));
    this._internalVars.queryResultsContainer.addEventListener("mouseout", _setIsOnSuggestionsFalse.bind(this));

}


function _clearSearch(): void {
    this._internalVars.addressSearchField.value = "";
    this._internalVars.queryResultsContainer.innerHTML = "";
    this._internalVars.queryResultsContainer.classList.add("hidden");
}

function callRadar(inputLength: number): void {
    if (inputLength !== this._internalVars.addressSearchField.value.length) {
        return;
    }

    this._internalVars.queryResultsContainer.classList.remove("hidden");
    const partialAddress: string = this._internalVars.addressSearchField.value;

    Radar.autocomplete(
        {
            query: `${partialAddress}`,
            near: this._options.queryParameters.near ? this._options.queryParameters.near : "",
            layers: this._options.queryParameters.layers ? this._options.queryParameters.layers : "address",
            limit: this._options.queryParameters.limit ? this._options.queryParameters.limit : 10,
            country: this._options.queryParameters.country ? this._options.queryParameters.country : "US, PR",
            expandUnits: this._options.queryParameters.expandUnits ? this._options.queryParameters.expandUnits : false,
        },
        (err: Error | null, result?: { addresses: any[] }) => {
            if (!err) {
                const suggestions = result.addresses;
                this._internalVars.displaySuggestedAddresses(suggestions);
            } else {
                console.log("There was an error", err);
            }
        }
    );
}


function _getAddresses(): void {
    const threshold = this._options.queryParameters.threshold ? this._options.queryParameters.threshold : 3;
    const debounceMS = this._options.queryParameters.debounceMS ? this._options.queryParameters.debounceMS - 200 : 200;

    if (this._internalVars.addressSearchField.value.length >= threshold) {
        setTimeout(callRadar.bind(this, this._internalVars.addressSearchField.value.length), debounceMS);
    } else {
        this._internalVars.queryResultsContainer.classList.add("hidden");
    }
}


function _displaySuggestedAddresses(suggestions: object): void {
    this._internalVars.queryResultsContainer.innerHTML = "";
    Array.prototype.forEach.call(suggestions, (suggestion: any) => {
        const address = suggestion.formattedAddress;
        const suggestionElement = document.createElement("div");
        suggestionElement.classList.add("query-results-item");
        const icon = document.createElement("span");
        icon.classList.add("geico-icon", "icon-geolocation", "geico-icon--small");
        suggestionElement.appendChild(icon);
        const text = document.createElement("span");
        text.textContent = address;
        suggestionElement.appendChild(icon);
        suggestionElement.appendChild(text);
        this._internalVars.queryResultsContainer.appendChild(suggestionElement);
        suggestionElement.addEventListener("click",_selectAddressEventHandler.bind(this, suggestion));
    });
}

function _fillInAddress(addressObject: object): void {

    this._internalVars.selectedAddressObject = addressObject;

    // Reset All Fields
    this._internalVars.queryResultsContainer.innerHTML = "";
    this._internalVars.queryResultsContainer.classList.add("hidden");
    const address1Element = this._internalVars.node.querySelector(".address_1");
    if (address1Element) {
        address1Element.value = "";
        address1Element.disabled = false;
    }

    for (const component in this._internalVars.componentForm) {
        if (component !== "street_number" && component !== "route" && this._internalVars.node.querySelector("." + component) !== null) {
            const element = this._internalVars.node.querySelector("." + component);
            element.value = "";
            element.disabled = false;
        }
    }

    this._internalVars.address1Element = this._internalVars.node.querySelector(".address_1");
    this._internalVars.address2Element = this._internalVars.node.querySelector(".address_2");
    this._internalVars.aptNumElement = this._internalVars.node.querySelector(".apt_num");
    this._internalVars.city = this._internalVars.node.querySelector(".locality");
    this._internalVars.state = this._internalVars.node.querySelector(".administrative_area_level_1");
    this._internalVars.postalCode = this._internalVars.node.querySelector(".postal_code");

    // Get each component of the address from the place details and fill the corresponding field on the form.
    if (this._internalVars.address1Element) {
        const value = this._internalVars.selectedAddressObject.addressLabel ? this._internalVars.selectedAddressObject.addressLabel : ((this._internalVars.selectedAddressObject.number as string) + " " + (this._internalVars.selectedAddressObject.street as string));
        this._internalVars.address1Element.value = value;
    }
    if (this._internalVars.city) {
        this._internalVars.city.value = this._internalVars.selectedAddressObject.city;
    }
    if (this._internalVars.state) {
        this._internalVars.state.value = this._internalVars.selectedAddressObject.stateCode;
    }
    if (this._internalVars.postalCode) {
        this._internalVars.postalCode.value = this._internalVars.selectedAddressObject.postalCode;
    }
    if (this._internalVars.country) {
        this._internalVars.country.value = this._internalVars.selectedAddressObject.country;
    }
    _clearSearch.call(this);
}

function _setIsOnSuggestionsTrue(): void {
    this._internalVars.isOnSuggestions = true;
}

function _setIsOnSuggestionsFalse(): void {
    this._internalVars.isOnSuggestions = false;
}

function _inputAddressSearchFieldEventHandler(): void {
    this._internalVars.getAddresses();
}


function _blurAddressSearchFieldEventHandler(): void {
    if (!this._internalVars.isOnSuggestions && !this._internalVars.queryResultsContainer.classList.contains("hidden")) {
        this._internalVars.queryResultsContainer.classList.add("hidden");
        const element = event.target as HTMLInputElement;
        if (this._internalVars.queryResultsContainer && this._internalVars.queryResultsContainer.children.length === 0 && element.value.length !== 0 && this._internalVars.address1Element.value.length === 0) {
            this._internalVars.address1Element.value = element.value;
            element.value = "";
        }
    }
}

function _focusAddressSearchFieldEventHandler(): void {
    const threshold = this._options.queryParameters.threshold ? this._options.queryParameters.threshold : 3;
    if (this._internalVars.addressSearchField.value.length >= threshold) {
        this._internalVars.queryResultsContainer.classList.remove("hidden");
    }
    const element = event.target as HTMLInputElement;
    if (element && this._internalVars.queryResultsContainer && this._internalVars.queryResultsContainer.children.length === 0 && element.value !== null && this._internalVars.address1Element.value === null) {
        this._internalVars.address1Element.value = element.value;
        element.value = "";
    }
}

function _selectAddressEventHandler(selectedAddressObject: object): void {
    _fillInAddress.call(this, selectedAddressObject);
    if (this._options.onSelectAddress) {
        this._options.onSelectAddress();
    }
}


Version.initGdkNPM(component, versions, GdkAddressAutoComplete);

export { GdkAddressAutoComplete };